import React, { FC } from 'react';
import i18n from 'i18n-js';

import { font } from '@styles/fonts';
import Svg from '@styles/icons/svg';

import DateHeader from './components/DateHeader';

import Jobs from './components/Jobs';
import Alarms from './components/Alarms';
import NCFileType from './components/NcFileType';
import ProductivityStatus from './components/ProductivityStatus';
import Counts from './components/Counts';
import Timeline from './components/Timeline';

import NCFileDetailsModalWrapper from '@components/wallboard/components/NCFileDetailsModalWrapper';

import FeatureFlags from '@components/featureFlags';
import LabelPopup from './components/LabelPopup';
import ColorWrapper from '@styles/colors';
import { setSelectedNCFile as setSelectedNCFileManufacture } from '@reducers/manufacture';
import { SCREEN_BORDER_RADIUS, SCREEN_PADDING_HORIZONTAL, SCREEN_PADDING_VERTICAL_FOR_ITEM, TEST_IDS } from '@v2/pages/common/constants/base.constants';
import { setSelectedChartDate, setSelectedChartDateEnd } from '@reducers/machinesV2';
import { NCFileDetailsScreenName } from '@v2/pages/nc-file/details/navigation/config';
import { NCFileDetailsScreen } from '@v2/pages/nc-file/details/common/enums/ncFileDetails.enum';
import { Perspective } from '@v2/pages/common/enums';
import { scale } from '@components/frame';
import { TimelineBlockTitle } from './components/TimelineBlockTitle';
import { TIMELINE_EVENTS_ORDER_BY, OMITTED_EVENT_MENU_ITEMS, TIMELINE_CONFIG, TIMELINE_LINE_CONFIG } from './constants';
import { TimelineBatches } from './components/TimelineBatches';
import TimelineShimmer from './components/TimelineShimmer';
import NCFileBatchEvents from '@v2/pages/common/components/NCFileBatchEvents';
import { NCFileBatchEventTypeMenu } from '@v2/pages/machines/common/components/Header/components';
import CloseButton from '@components/closeButton';
import { ColorFunction } from '@styles/colors/types';
import { useHandleMainDetailsScreenNavigation } from '@v2/pages/common/hooks/useHandleMainDetailsScreenNavigation';
import { getEventsContainerHeight } from './helpers';
import { NCFileSearchItem } from '@interfaces/search';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import {
  useManageTimelineActions,
  useManageTimelineEffects,
  useManageTimelinePopup,
  useTimelineRange,
  useTimelineSelectors,
  useTimelineSizes,
  useTimelineViewModel,
} from './hooks';
import { getUTCFormatOfLocalTime, withERP } from '@helpers';

export type Popup = 'productivityStatus' | 'productivityStatusLabel' | 'alarm' | 'ncFileName';

interface TimelineProps {
  C: ColorFunction;
  fullScreen?: boolean;
}

const TimelineComponent: FC<TimelineProps> = ({ C, fullScreen = true }) => {
  const dispatch = useAppDispatch();

  const { startTime, endTime, yearIsEnabled } = useTimelineRange();
  const { selectedTimeCounter, isWithERP, selectedMachine } = useTimelineSelectors();

  const {
    detailsWidth,
    blockWidth,
    popupWidthProductivityStatus,
    popupWidthProductivityStatusLabel,
    popupWidthProductivityStatusLabelSymbolSize,
    popupHeightProductivityStatusLabel,
    popupWidthAlarm,
    popupWidthAlarmSymbolSize,
    popupHeightAlarm,
    popupWidthNcFileName,
    popupWidthNcFileNameSymbolSize,
    popupHeightNcFileName,
  } = useTimelineSizes({ fullScreen });

  const {
    fetchData,
    abortControllerEvents,
    abortControllerProductionBatches,
    abortControllerAlarmEvent,
    abortControllerMachineStatistics,
    events,
    productionBatchesAndCounts,
    machineStatistics,
    alarmEventGroups,
    enhancedBatchesData,
  } = useTimelineViewModel({ startTime, endTime, yearIsEnabled });

  const {
    popupConfigAlarm,
    popupConfigNcFileName,
    clearPopupTimeout,
    popupConfigProductivityStatus,
    popupConfigProductivityStatusLabel,
    triggerHidePopup,
    triggerShowPopup,
  } = useManageTimelinePopup({
    sizes: {
      blockWidth,
      popupWidthProductivityStatus,
      popupWidthProductivityStatusLabel,
      popupWidthProductivityStatusLabelSymbolSize,
      popupWidthAlarm,
      popupWidthAlarmSymbolSize,
      popupWidthNcFileName,
      popupWidthNcFileNameSymbolSize,
    },
  });

  const {
    rules,
    abortControllerEventStart,
    abortControllerEventEnd,
    abortControllerNcFile,
    selectedTime,
    selectRange,
    handleMouseMove,
    onMouseLeave,
    handleMouseDown,
    handleMouseUp,
    lineRef,
    timeXRef,
    sectionRef,
    timelineRef,
    onShowEventsPress,
    eventsContainerRef,
    selectedEventType,
    setSelectedEventType,
    closeEventsForMachine,
    isShowingEventsForMachine,
  } = useManageTimelineActions({ startTime, endTime, yearIsEnabled, fetchData, triggerHidePopup, sizes: { blockWidth } });

  useManageTimelineEffects({
    abortControllerEventStart,
    abortControllerEventEnd,
    abortControllerNcFile,
    abortControllerEvents,
    abortControllerProductionBatches,
    abortControllerAlarmEvent,
    abortControllerMachineStatistics,
    startTime,
    endTime,
    yearIsEnabled,
    fetchData,
  });

  const { navigateToMainDetailsScreen } = useHandleMainDetailsScreenNavigation();

  // For single machine timeline, check if current machine is showing events
  const showEvents = selectedMachine ? isShowingEventsForMachine(selectedMachine.machineId) : false;

  return (
    <div
      data-testid={TEST_IDS.timeline.container}
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        userSelect: 'none',
        paddingTop: SCREEN_PADDING_VERTICAL_FOR_ITEM,
        width: detailsWidth - SCREEN_PADDING_HORIZONTAL * 2,
        marginInline: 'auto',
        paddingInline: SCREEN_PADDING_HORIZONTAL,
        overflow: 'auto',
      }}
    >
      <div
        style={{
          width: '100%',
          ...(!showEvents ? { height: '100%' } : {}),
          display: 'flex',
          justifyContent: 'center',
          alignContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
          background: C('Background/Light/Field'),
          borderRadius: scale(SCREEN_BORDER_RADIUS),
          boxShadow: '0px 0px 6px rgba(0, 0, 0, 0.1)',
        }}
      >
        <DateHeader
          {...{
            startTime,
            endTime,
            C,
            setSelectedChartDate: (date: string) => dispatch(setSelectedChartDate(date)),
            setSelectedChartDateEnd: (date: string) => dispatch(setSelectedChartDateEnd(date)),
            selectedTime,
            isZoomInDisabled: rules.isZoomInDisabled,
            isZoomResetDisabled: rules.isZoomResetDisabled,
            setEndTime: (date: string) => dispatch(setSelectedChartDateEnd(date)),
            setStartTime: (date: string) => dispatch(setSelectedChartDate(date)),
            fetchData,
            selectRange,
            selectedTimeCounter,
            resetOnInitialRender: false,
          }}
        />
        <div style={{ width: blockWidth, height: '100%', position: 'relative' }}>
          <div
            id="mainContainer"
            style={{ position: 'relative', width: blockWidth, height: '100%' }}
            onMouseMove={handleMouseMove}
            onMouseLeave={onMouseLeave}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
          >
            <div
              ref={lineRef}
              style={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                width: 1,
                backgroundColor: C('System/Light/Red50%'),
                zIndex: 1000,
              }}
            >
              <Svg icon="triangle" color="System/Light/Red" height={9} width={13} style={{ position: 'absolute', left: -6 }} />
              <div
                ref={timeXRef}
                style={{
                  ...font['Subhead 1'],
                  fontSize: 15,
                  fontWeight: 400,
                  lineHeight: '20px',
                  width: 44,
                  textAlign: 'center',
                  backgroundColor: C('System/Light/Red'),
                  color: C('Label/Light/AlwaysLight Primary'),
                  zIndex: 1,
                  position: 'absolute',
                  top: 13,
                  left: -26,
                  borderRadius: 4,
                  fontVariant: ['tabular-nums'],
                  justifyContent: 'center',
                  alignContent: 'center',
                  alignItems: 'center',
                  display: 'flex',
                  paddingLeft: 4,
                  paddingRight: 4,
                }}
              />
            </div>
            <div
              ref={sectionRef}
              style={{
                position: 'absolute',
                height: '100%',
                backgroundColor: 'rgba(0, 120, 215, 0.5)',
                pointerEvents: 'none',
                zIndex: 1000,
              }}
            />
            <div style={{ width: '100%', height: 1, background: C('Gray/Light/3') }} />
            <Timeline
              {...{
                C,
                startTime,
                timelineRef,
                blockWidth,
                rules,
              }}
            />
            <Counts
              {...{
                C,
                countsData: { loaded: productionBatchesAndCounts.loaded, data: productionBatchesAndCounts.data?.counts },
                blockWidth,
                startTime,
                rules,
              }}
            />
            <ProductivityStatus
              {...{
                C,
                events,
                blockWidth,
                rules,
                triggerShowPopup,
                triggerHidePopup,
                timelineRef,
                startTime,
                netUtilization: machineStatistics?.data?.teep,
                onShowEventsPress: () => onShowEventsPress(selectedMachine?.machineId),
                showEvents,
                machineId: selectedMachine?.machineId || 0,
              }}
            />
            {!showEvents && (
              <>
                {withERP(isWithERP) && (
                  <TimelineBatches
                    C={C}
                    batchesData={enhancedBatchesData}
                    blockWidth={blockWidth}
                    rules={rules}
                    startTime={startTime}
                    events={events}
                  />
                )}
                {popupConfigProductivityStatus.show && (
                  <NCFileDetailsModalWrapper
                    {...{
                      setSelectedNCFile: (ncFile: NCFileSearchItem) => dispatch(setSelectedNCFileManufacture(ncFile)),
                      C,
                      popupType: 'productivityStatus',
                      popupConfig: popupConfigProductivityStatus,
                      popupWidth: popupWidthProductivityStatus,
                      clearPopupTimeout,
                      triggerHidePopup,
                      abortControllerEventStart,
                      abortControllerEventEnd,
                      abortControllerNcFile,
                    }}
                  />
                )}
                {popupConfigProductivityStatusLabel.show && (
                  <LabelPopup
                    {...{
                      C,
                      popupName: 'productivityStatusLabel',
                      popupConfig: popupConfigProductivityStatusLabel,
                      popupWidth:
                        (popupConfigProductivityStatusLabel?.item?.ncFileMain?.length ?? 0) * popupWidthProductivityStatusLabelSymbolSize +
                        popupWidthProductivityStatusLabel,
                      popupHeight: popupHeightProductivityStatusLabel,
                      clearPopupTimeout,
                      triggerHidePopup,
                    }}
                  />
                )}

                {withERP(isWithERP) && (
                  <FeatureFlags {...{ name: 'timeline-jobs' }}>
                    <Jobs
                      {...{
                        C,
                        blockWidth,
                        startTime,
                        rules,
                        batches: enhancedBatchesData,
                        events,
                      }}
                    />
                  </FeatureFlags>
                )}

                <TimelineBlockTitle containerStyle={{ marginTop: TIMELINE_CONFIG.gapBetweenBlocks }} C={C}>
                  {i18n.t('Alarms')}
                </TimelineBlockTitle>
                {!alarmEventGroups.data.length && alarmEventGroups.loaded && (
                  <div
                    style={{
                      width: '100%',
                      height: TIMELINE_LINE_CONFIG.height,
                      backgroundColor: C(TIMELINE_LINE_CONFIG.backgroundColor),
                      borderRadius: TIMELINE_LINE_CONFIG.borderRadius,
                    }}
                  />
                )}
                {alarmEventGroups.loaded ? (
                  alarmEventGroups.data.map((alarmEvents) => (
                    <Alarms
                      {...{
                        C,
                        triggerShowPopup,
                        triggerHidePopup,
                        alarmEvents,
                        blockWidth,
                        startTime,
                        rules,
                        timelineRef,
                      }}
                    />
                  ))
                ) : (
                  <TimelineShimmer style={{ height: TIMELINE_LINE_CONFIG.height }} />
                )}
                {popupConfigAlarm.show && (
                  <LabelPopup
                    {...{
                      C,
                      popupName: 'alarm',
                      popupConfig: popupConfigAlarm,
                      popupWidth: (popupConfigAlarm?.item?.errorText?.length ?? 0) * popupWidthAlarmSymbolSize + popupWidthAlarm,
                      popupHeight: popupHeightAlarm,
                      clearPopupTimeout,
                      triggerHidePopup,
                      textColor: 'Label/Light/Primary',
                    }}
                  />
                )}

                <FeatureFlags {...{ name: 'timeline-nc-file-type' }}>
                  <NCFileType
                    {...{
                      C,
                      triggerShowPopup,
                      triggerHidePopup,
                      batches: enhancedBatchesData,
                      blockWidth,
                      rules,
                      timelineRef,
                      startTime,
                      events,
                    }}
                  />
                </FeatureFlags>
                {popupConfigNcFileName.show && (
                  <LabelPopup
                    {...{
                      C,
                      popupName: 'ncFileName',
                      popupConfig: popupConfigNcFileName,
                      popupWidth: (popupConfigNcFileName?.item?.ncFileName?.length ?? 0) * popupWidthNcFileNameSymbolSize + popupWidthNcFileName,
                      popupHeight: popupHeightNcFileName,
                      clearPopupTimeout,
                      triggerHidePopup,
                      action: (data: string) => {
                        navigateToMainDetailsScreen({
                          mainScreen: NCFileDetailsScreenName,
                          subScreen: NCFileDetailsScreen.NC_FILE,
                          params: {
                            perspective: Perspective.NC_FILE,
                            ncFileName: popupConfigNcFileName?.item?.ncFileName,
                            ncFileId: popupConfigNcFileName?.item?.ncFileId,
                            ncFileStatus: popupConfigNcFileName?.item?.ncFileStatus,
                          },
                        });
                      },
                    }}
                  />
                )}
              </>
            )}
          </div>
        </div>
      </div>
      {showEvents && (
        <div
          ref={eventsContainerRef}
          data-testid={TEST_IDS.timeline.eventsPanel}
          onMouseDown={(e) => e.stopPropagation()}
          onMouseUp={(e) => e.stopPropagation()}
          onMouseMove={(e) => e.stopPropagation()}
          style={{ position: 'relative', width: '100%' }}
        >
          <div style={{ position: 'absolute', top: 9, right: 0, zIndex: 10000 }}>
            <CloseButton C={C} onPress={() => closeEventsForMachine(selectedMachine?.machineId || 0)} />
          </div>
          <NCFileBatchEventTypeMenu
            checkIsSelected={(eventType) => eventType === selectedEventType}
            onPress={(eventType) => setSelectedEventType(eventType)}
            rowStyles={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', marginVertical: 12, flex: 1 }}
            omitItems={OMITTED_EVENT_MENU_ITEMS}
          />
          <div>
            <NCFileBatchEvents
              eventType={selectedEventType}
              machineIds={[selectedMachine.machineId]}
              webTabletContainerStyle={{ height: getEventsContainerHeight(eventsContainerRef) }}
              startedOn={getUTCFormatOfLocalTime(startTime)}
              endedOn={getUTCFormatOfLocalTime(endTime)}
              withoutTimeTiles
              customOrderBy={TIMELINE_EVENTS_ORDER_BY}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ColorWrapper(TimelineComponent);
